import { useEffect, useState } from 'react'
import { useNow } from '../../hooks'

/*
 * The center's clock in the browser (decision 37): answers of /api/admin/relay/*,
 * /api/admin/nodes* and /api/admin/outputs* carry X-Server-Time, every SSE event
 * of /api/admin/relay/events carries server_time. The console keeps the offset
 * "center − browser" and measures every age (data N s old, "4 min ago") on the
 * center's clock, so a browser clock that is off by ±30 s does not make fresh
 * data look stale or stale data look fresh.
 *
 * A REST answer gives the better sample: the offset is taken against the middle
 * of the request (sent … arrived), so the one-way delay mostly cancels out. An
 * SSE event has no request time: it is used only while no REST sample is newer
 * than REST_FRESH_MS (its error is the one-way delay of the stream, well under a
 * second on a working network).
 */

type Listener = () => void

const REST_FRESH_MS = 60_000

let offset = 0
let known = false
let restAt = 0
const listeners = new Set<Listener>()

function set(next: number) {
  const changed = !known || Math.abs(next - offset) >= 1
  offset = next
  known = true
  if (changed) for (const l of listeners) l()
}

/** X-Server-Time of an answer: sentAt / arrivedAt are browser times around the request. */
export function noteServerTimeRest(iso: string | null | undefined, sentAt: number, arrivedAt: number) {
  const t = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(t)) return
  restAt = arrivedAt
  set(t - (sentAt + arrivedAt) / 2)
}

/** server_time of an SSE event that arrived at arrivedAt (browser time). */
export function noteServerTimeEvent(iso: string | null | undefined, arrivedAt: number) {
  const t = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(t)) return
  if (known && arrivedAt - restAt < REST_FRESH_MS) return
  set(t - arrivedAt)
}

/** Center minus browser, ms (0 until the first sample). */
export const clockOffset = () => offset

/** The center has told its time at least once. */
export const clockKnown = () => known

/** Now on the center's clock (ms since the epoch). */
export const centerNow = () => Date.now() + offset

/** Browser time → center time. */
export const toCenter = (browserMs: number) => browserMs + offset

export function onClockChange(l: Listener): () => void {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

/** Tests and a new session: forget the offset. */
export function resetClock() {
  offset = 0
  known = false
  restAt = 0
}

/** useNow on the center's clock: ticks every `ms`, re-renders when the offset changes. */
export function useCenterNow(ms = 1000): number {
  const now = useNow(ms)
  const [, bump] = useState(0)
  useEffect(() => onClockChange(() => bump((x) => x + 1)), [])
  return toCenter(now)
}
